import { dateLocale } from "@/lib/dateTime";
import { Link } from "react-router-dom";
import { ArrowLeft, Check, Mail, Pencil, Phone, Pin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { VisitActions } from "@/components/visits/VisitActions";
import { StatusPill } from "@/components/patterns";
import { WhatsAppIcon } from "@/components/whatsapp/WhatsAppIcon";
import { cn, formatINRCompact } from "@/lib/utils";
import type { DealRecord } from "@/types";
import { fmtDay, type DealSummary } from "./types";

/** "2 h ago", "yesterday", "12 Oct" */
export function timeAgo(iso?: string | null) {
  if (!iso) return "—";
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 2) return "just now";
  if (mins < 60) return `${mins} min ago`;
  if (mins < 60 * 24) return `${Math.floor(mins / 60)} h ago`;
  if (mins < 60 * 48) return "yesterday";
  return new Date(iso).toLocaleDateString(dateLocale(), { day: "numeric", month: "short" });
}

/** Title, who owns it, call / WhatsApp / email, the figures that matter, and the road to Won as an arrow path */
export function DealRecordHeader({
  deal,
  summary,
  busy,
  canWrite,
  lastTouch,
  onBack,
  onEdit,
  onSendQuote,
  onMarkLost,
  onMarkWon,
  onStageClick,
  onCall,
  onWhatsApp,
  onEmail,
  pinned,
  onTogglePin,
}: {
  deal: DealRecord;
  summary: DealSummary | null;
  busy: boolean;
  canWrite: boolean;
  /** The newest thing that happened on the deal, for "Last touch" */
  lastTouch: { at: string; what: string } | null;
  onBack: () => void;
  onEdit: () => void;
  onSendQuote: () => void;
  onMarkLost: () => void;
  onMarkWon: () => void;
  onStageClick: (stageId: string) => void;
  onCall?: () => void;
  onWhatsApp?: () => void;
  onEmail?: () => void;
  pinned: boolean;
  onTogglePin: () => void;
}) {
  const isWon = deal.deal_status === "WON";
  const isLost = deal.deal_status === "LOST";
  const open = !isWon && !isLost;
  const owner = deal.owner ? `${deal.owner.first_name} ${deal.owner.last_name || ""}`.trim() : "Unassigned";
  const closeIn = deal.expected_close_date ? Math.round((new Date(deal.expected_close_date).getTime() - Date.now()) / 86400000) : null;
  const facts: { label: string; value: string; sub: string; tone?: string }[] = [
    { label: "Amount", value: formatINRCompact(deal.amount), sub: "deal value" },
    { label: "Weighted", value: formatINRCompact(deal.weighted_amount), sub: `${Number(deal.probability || 0)}% probability` },
    { label: "Expected close", value: fmtDay(deal.expected_close_date), sub: closeIn === null ? "no date set" : closeIn >= 0 ? `in ${closeIn} days` : `${-closeIn} days ago`, tone: open && closeIn !== null && closeIn < 0 ? "text-danger" : undefined },
    { label: "Last touch", value: lastTouch ? timeAgo(lastTouch.at) : "—", sub: lastTouch?.what || "nothing logged yet", tone: lastTouch && Date.now() - new Date(lastTouch.at).getTime() < 7 * 86400000 ? "text-success" : "text-warning" },
  ];

  return (
    <section aria-label="Deal summary" className="flex flex-col gap-3.5 rounded-xl border border-border bg-card px-[18px] py-4">
      <div className="flex flex-wrap items-start gap-3">
        <Button variant="outline" size="icon-sm" aria-label="Back to deals" onClick={onBack} className="mt-0.5">
          <ArrowLeft className="size-4" />
        </Button>
        <div className="flex min-w-[260px] flex-[1_1_380px] flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2.5">
            <h1 className="text-[22px] font-semibold leading-tight tracking-tight">{deal.name}</h1>
            <StatusPill tone={isWon ? "gold" : isLost ? "danger" : "primary"}>{isWon ? "Won" : isLost ? "Lost" : "Open"}</StatusPill>
            {open && summary?.origin?.priority === "HOT" && <StatusPill tone="warning">Hot</StatusPill>}
            {open && summary?.origin?.priority === "WARM" && <StatusPill tone="neutral">Warm</StatusPill>}
          </div>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[12.5px] text-muted-foreground">
            {deal.account_id && (
              <Link to={`/accounts/${deal.account_id}`} className="font-semibold text-primary hover:underline">
                {deal.account?.name || "Account"}
              </Link>
            )}
            <span aria-hidden="true">·</span>
            <span className="font-mono">{deal.deal_number || `#${deal.id.slice(0, 8)}`}</span>
            <span aria-hidden="true">·</span>
            <span>
              Owner <strong className="font-medium text-foreground">{owner}</strong>
            </span>
            {summary?.origin?.source && (
              <>
                <span aria-hidden="true">·</span>
                <Link to={`/leads/${summary.origin.lead_id}`} className="hover:underline" title="Open the lead this deal came from">
                  from {summary.origin.source}, {new Date(summary.origin.created_on).toLocaleDateString(dateLocale(), { day: "numeric", month: "short" })}
                </Link>
              </>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {open && onCall && (
            <Button variant="outline" size="sm" onClick={onCall}>
              <Phone className="mr-1.5 size-3.5" />
              Call
            </Button>
          )}
          {open && onWhatsApp && (
            <Button variant="outline" size="sm" onClick={onWhatsApp}>
              <WhatsAppIcon className="mr-1.5 size-3.5" />
              WhatsApp
            </Button>
          )}
          {open && onEmail && (
            <Button variant="outline" size="sm" onClick={onEmail}>
              <Mail className="mr-1.5 size-3.5" />
              Email
            </Button>
          )}
          {open && deal.account_id && <VisitActions customer={{ type: "ACCOUNT", id: deal.account_id, label: deal.account?.name || "Account", dealId: deal.id, dealLabel: deal.name }} />}
          {open && <span aria-hidden="true" className="mx-1 h-[22px] w-px bg-border" />}
          <Button variant="outline" size="icon-sm" aria-pressed={pinned} aria-label={pinned ? "Unpin from Home" : "Pin to Home"} title={pinned ? "Unpin from Home" : "Pin to Home"} onClick={onTogglePin}>
            <Pin className={`size-3.5 ${pinned ? "fill-current text-primary" : ""}`} />
          </Button>
          {canWrite && (
            <Button variant="outline" size="sm" onClick={onEdit}>
              <Pencil className="mr-1.5 size-3.5" />
              Edit
            </Button>
          )}
          {open && (
            <>
              <Button variant="outline" size="sm" onClick={onSendQuote}>
                Send quote
              </Button>
              <Button variant="outline" size="sm" className="text-danger hover:text-danger" onClick={onMarkLost} disabled={busy}>
                Mark lost
              </Button>
              <Button size="sm" className="bg-sidebar text-white hover:bg-sidebar/90" onClick={onMarkWon} disabled={busy}>
                <span className="mr-1.5 size-2 rounded-full bg-gold" />
                Mark won
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
        {summary && (
          <div className="min-w-0 flex-[1_1_520px] overflow-x-auto">
            <ol aria-label="Pipeline stages" className="flex min-w-[560px]">
              {summary.stages.map((s, i) => {
                const won = s.key === "WON";
                const bg = s.state === "done" ? "bg-success-soft" : s.state === "now" ? (isWon && won ? "bg-gold" : "bg-primary") : "bg-muted";
                const ink = s.state === "done" ? "text-success" : s.state === "now" ? (isWon && won ? "text-sidebar" : "text-white") : "text-muted-foreground";
                const clip = i === 0 ? "polygon(0 0, calc(100% - 12px) 0, 100% 50%, calc(100% - 12px) 100%, 0 100%)" : "polygon(0 0, calc(100% - 12px) 0, 100% 50%, calc(100% - 12px) 100%, 0 100%, 12px 50%)";
                return (
                  <li key={s.id} className={cn("min-w-0 flex-1", i > 0 && "-ml-1.5")}>
                    <button
                      type="button"
                      disabled={busy || s.state === "now"}
                      onClick={() => onStageClick(s.id)}
                      title={s.state === "now" ? "Current stage" : `Move to ${s.name}`}
                      style={{ clipPath: clip }}
                      className={cn("flex h-10 w-full flex-col justify-center text-left disabled:cursor-default", i > 0 ? "pl-[22px] pr-4" : "pl-3.5 pr-4", bg, ink)}
                    >
                      <span className="flex items-center gap-1 whitespace-nowrap text-[12.5px] font-semibold">
                        {s.state === "done" && <Check className="size-3" strokeWidth={3} />}
                        <span className="truncate">{s.name}</span>
                      </span>
                      <span className="whitespace-nowrap text-[11px] opacity-85">{s.days !== null ? `${s.days} ${s.days === 1 ? "day" : "days"}` : " "}</span>
                    </button>
                  </li>
                );
              })}
            </ol>
          </div>
        )}
        <div className="flex flex-wrap gap-x-[18px] gap-y-2">
          {facts.map((f) => (
            <div key={f.label} className="flex min-w-[92px] flex-col">
              <span className="text-[11px] text-muted-foreground">{f.label}</span>
              <span className={cn("font-mono text-sm font-semibold", f.tone)}>{f.value}</span>
              <span className="text-[11px] text-muted-foreground">{f.sub}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
